import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  REPORT_COLUMN_ANALYSIS_SUMMARY_KEY,
  REPORT_COLUMN_SEPARATE_ANALYSIS_SUMMARY_LABEL,
  ReportColumn,
} from '@features/Admin/Campaign/constants/reportColumns.constants';

interface ReportColumnsDialogProps {
  open: boolean;
  onClose: () => void;
  onExport: () => void | Promise<void>;
  reportColumns: readonly ReportColumn[];
  selectedAdditionalColumns: string[];
  onToggleColumn: (columnKey: string) => void;
  separatedAnalysisSummary: boolean;
  onSeparatedAnalysisSummaryChange: (value: boolean) => void;
}

export const ReportColumnsDialog: React.FC<ReportColumnsDialogProps> = ({
  open,
  onClose,
  onExport,
  reportColumns,
  selectedAdditionalColumns,
  onToggleColumn,
  separatedAnalysisSummary,
  onSeparatedAnalysisSummaryChange,
}) => {
  const { t } = useTranslation();

  const reportColumnsHalfIndex = Math.ceil(reportColumns.length / 2);
  const reportColumnsFirstColumn = reportColumns.slice(0, reportColumnsHalfIndex);
  const reportColumnsSecondColumn = reportColumns.slice(reportColumnsHalfIndex);

  const analysisSummarySelected = selectedAdditionalColumns.includes(
    REPORT_COLUMN_ANALYSIS_SUMMARY_KEY,
  );

  const renderColumnRow = (column: ReportColumn) => {
    const isChecked = column.required ? true : selectedAdditionalColumns.includes(column.key);

    return (
      <Box key={column.key}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked}
              disabled={column.required}
              onChange={() => onToggleColumn(column.key)}
            />
          }
          label={
            column.required
              ? `${column.label} (${t('campaignPage.ongoingCallsTable.report.required')})`
              : column.label
          }
        />
        {column.key === REPORT_COLUMN_ANALYSIS_SUMMARY_KEY && (
          <Box sx={{ pl: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={separatedAnalysisSummary}
                  disabled={!analysisSummarySelected}
                  onChange={(e) => onSeparatedAnalysisSummaryChange(e.target.checked)}
                />
              }
              label={REPORT_COLUMN_SEPARATE_ANALYSIS_SUMMARY_LABEL}
            />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('campaignPage.ongoingCallsTable.report.columnsTitle')}</DialogTitle>
      <DialogContent dividers>
        {reportColumns.length === 0 && (
          <Typography>{t('campaignPage.ongoingCallsTable.report.columnsEmpty')}</Typography>
        )}
        {reportColumns.length > 0 && (
          <Box>
            <Typography sx={{ mb: 2 }} color="text.secondary">
              {t('campaignPage.ongoingCallsTable.report.columnsDescription')}
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <FormGroup>
                  {reportColumnsFirstColumn.map((column) => renderColumnRow(column))}
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormGroup>
                  {reportColumnsSecondColumn.map((column) => renderColumnRow(column))}
                </FormGroup>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('campaignPage.ongoingCallsTable.report.cancel')}</Button>
        <Button variant="contained" onClick={onExport}>
          {t('campaignPage.ongoingCallsTable.report.export')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
